import {computed, effect, inject, Injectable, InjectionToken, Signal, signal, untracked} from '@angular/core';
import {
  AppointmentSettingType, BookingStatusType,
  IMessageCode,
  JsonValue,
  MapError,
  MessageCode,
  ReasonType,
  Schema,
  State,
  States
} from "@mindpath/shared";
import {
  DefaultSupportLinkClass,
  DemographicsInfo,
  ErrorActionLabels,
  InsuranceInfo,
  LocallyRaisedDisplays,
  SelectedAppointmentInfo,
  SupportLinkClasses
} from "../statics";
import {LanguageIdMapping, SearchFilter} from "./finder.service";
import {UiService} from "./ui.service";
import {assertExists} from "../assert";
import {Option} from "../components/autocomplete/autocomplete.config";
import {DataService, type DetailedInsurance} from "./data.service";
import {config} from "../../config";
import {NaiveDateDeserializer} from "./online-scheduler.service";
import {Analytics, AnalyticsService} from '../analytics/analytics.service';
import {type NavInfo, NavService} from "./nav.service";
import DateUtils from "../../lib/shared/utils/date.utils";
import {CaptureError} from "../../lib/shared/errors";
import {tryCaptureStackTrace} from "../../lib/shared/errors/error-stack-handling";
import {PiwikService} from "../analytics/piwik";
import {
  AppError,
  AppErrorAction,
  AppErrorImpl,
  AppErrorOptions
} from "../../lib/shared/errors/app-error";
import {resolveErrorCopy} from "../errors/error-copy";

@Injectable({
  providedIn: 'root'
})
export class AppService {

  static readonly Clinician = new InjectionToken<Schema<'ClinicianDetailedInfo'>>('Clinician');
  #error = signal<AppError | undefined>(undefined);
  error = computed(() => this.#error());

  #ui = inject(UiService);
  #dataSvc = inject(DataService);
  #analytics = inject(AnalyticsService);
  #nav = inject(NavService);
  #piwik = inject(PiwikService);

  #options = {
    insurers: signal<readonly DetailedInsurance[]>([]),
    states: [State.AZ, State.CA, State.FL, State.NC, State.SC, State.TX],
    conditions: signal<readonly Schema<'Condition'>[]>([]),
    languages: Object.entries(LanguageIdMapping)
  }

  #data = signal<Omit<AppData, 'filters'> & {
    readonly filters: Omit<SearchFilter, 'state' | 'insurancePlanId' | 'insuranceId'>
  }>({
    state: State.None,
    nav: this.#nav.current(),
    filters: {
    },
    insurance: {},
  });

  #ready$ = signal(false);
  readonly ready = this.#ready$.asReadonly();

  #save(data: unknown) {
    sessionStorage.setItem(`online-intake-${config.version}`, JSON.stringify(data));
  }

  #load() {
    const json = sessionStorage.getItem(`online-intake-${config.version}`);
    if (json) {
      const data = NaiveDateDeserializer(JSON.parse(json)) as Omit<AppData, 'insurance'> & { insurance: AppData['insurance']};

      // try and load the appt time if it's not set (this is for backwards compatibility)
      try {
        if (data.appt) {
          if (!data.appt.time) {
            const parsedTime = DateUtils.tryParseTime(data.appt.slot.time);
            if (parsedTime) {
              (data.appt as {time: NonNullable<AppData['appt']>['time']}).time = parsedTime
            }
          }
        }

        if (!data.insurance) {
          data.insurance = {};
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        // silently ignore
      }
      this.#data.set(data);
    } else {
      this.#data.set({
        state: State.None,
        filters: {

        },
        insurance: {},
        nav: this.#nav.current(),
      });
    }
    // return Promise.resolve();
  }


  async init() {
    await Promise.all([
      this.#loadConditions(),
      this.#loadInsurerOptions(this.#data().state),
    ]);

    this.#ready$.set(true);
  }

  async #loadConditions() {
    const conditions = await this.#dataSvc.getConditions();
    this.#options.conditions.set(conditions);
  }

  async #loadInsurerOptions(state: State) {
    if (state === State.None) {
      this.#options.insurers.set([]);
    } else {
      // if the state changed we need to get the insurers for the new state
      const insurances = await this.#dataSvc.getInsurancesFor(state);
      this.#options.insurers.set(insurances);
    }

  }

  readonly options = Object.freeze({
    insurance: Object.freeze({
      insurers: computed(() => {
        return this.#options.insurers().toSorted(compareItem).map(toOptionItem);
      }),
      plans: computed(() => {
        const insurers = this.#options.insurers();
        const loader = (id: number|undefined) => {
          if (!id) {
            return [];
          }
          return (insurers.find(i => i.id === id)?.plans ?? [])
            .toSorted(compareItem)
            .map(toOptionItem);
        }
        const data = this.data();
        return {
          primary: computed(() => loader(data.insurance?.primary?.id ?? data.filters.insuranceId)),
          secondary: computed(() => loader(data.insurance?.secondary?.id)),
        }
      }),
    }),
    languages: computed(() => {
      const languages = this.#options.languages.slice();
      return languages.map(([key, value]) => ({
        value: value,
        label: key,
      }) satisfies Option<number>).toSorted(compareItem);
    }),
    states: computed(() => {
      const states = this.#options.states.slice();

      const options: Option<State>[] = states.map(s => {
          // eslint-disable-next-line
        const label = (States as any)[s.toString() as any].toString();
        return ({
          value: s,
          label,
          alternatives: [label]
        });
      });

      options.push({
        value: State.None,
        label: 'I don\'t see my state',
        isPinned: true,
      });
      return options.toSorted(compareItem);

    }),
    conditions: computed(() => {
      const conditions: Schema<'Condition'>[] = this.#options.conditions().slice();

      conditions.unshift({
        name: 'General Evaluation',
        description: 'A clinician will evaluate the patient\'s symptoms, listen to their concerns, and find a treatment plan that best supports their mental health journey.',
        id: -1,
        isPinned: true,
        sortOrder: 0,
      } as Schema<'Condition'>);

      return conditions
        .toSorted(compareItem)
        .map(toOptionItem);
    }),
  });


  constructor() {

    effect(() => {
      const error = this.#error();
      if (!error) {
        return;
      }
      untracked(() => {
        this.#analytics.fireEvent(this.#nav.currentPage(), Analytics.EventType.Error, {
          action: 'code',
          data: error.messageCode.code
        }, {force: true});
      });

    });

    effect(() => {
      const navInfo = this.#nav.current();
      if (!this.ready()) {
        return;
      }
      this.#data.update(data => {
        return {
          ...data,
          nav: navInfo
        };
      });
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).app = this;

    const fieldsToBind = [
      ['appointmentSetting', 'choseAppointmentSetting'],
      ['settingPreference', 'choseSettingPreference'],
      ['genderId', 'choseGenderPreference'],
      ['typeOfCare', 'choseTypeOfCare'],
      ['timeOfDay', 'choseTimeOfDayPreference'],
      ['weeksOut', 'choseWeeksOutPreference'],
      ['dayOfWeek', 'choseDaysOfWeek'],
      ['conditionId', 'choseCondition'],
      ['languageId', 'chosePreferredLanguage'],
      ['ageRange', 'choseAgeRange'],
      ['location', 'choseLocationPreference'],
      ['clinician', 'clinicianSearchChanged'],
      ['insurance', 'choseInsurer'],
      ['insurancePlan', 'choseInsurerPlan'],
      ['eligibility', 'selectedEligibility'],
    ] as const satisfies [
      string,
      {
        [key in keyof AppService]: AppService[key] extends Signal<unknown> ? never : AppService[key] extends // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (...arg: any[]) => any ? key : never
      }[keyof AppService]
    ][];

    fieldsToBind.map(([specifier, methodName]) => {
      const method = this[methodName];
      this[methodName] = ((...args: Parameters<(typeof method)>): ReturnType<(typeof method)> => {
        this.#analytics.fireFieldChange(this.#nav.currentPage(), specifier, (args.length === 1 ? args[0] : args) as JsonValue);
        return (method as (...args: Parameters<typeof method>) => ReturnType<typeof method>).apply(this, args);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any;
    });

    this.#load();
    effect(() => {
      const ready = this.ready();
      const data = this.#data();
      if (!ready) {
        return;
      }
      this.#save(data);
    });
    effect(() => {
      const data = this.data();
      const ready = this.ready();
      if (!ready) {
        return;
      }
      this.#piwik.trackDimensions(data);
    })
  }

  resetFilters() {
    this.#data.update(data => ({
      ...data,
      filters: {
      },
      // insurance: undefined,
    }));


    [
      ['settingPreference', undefined],
      ['genderId', undefined],
      ['typeOfCare', undefined],
      ['timeOfDay', undefined],
      ['weeksOut', 0],
      ['dayOfWeek', undefined],
      ['conditionId', -1],
      ['languageId', undefined],
      ['ageRange', undefined],
      ['clinician', undefined],
      // ['insurance', undefined],
      // ['insurancePlan', undefined],
    ].forEach(([key, value]) => {
      this.#analytics.fireFieldChange(this.#nav.currentPage(), key as string, value, {skipCheck: true});
    });
  }

  async reset() {
    this.#error.set(undefined);
    this.#data.set({
      state: State.None,
      filters: {
      },
      nav: this.#nav.current(),
      insurance: {},
    });
    this.#save(this.#data());
  }

  clearError() {
    this.#error.set(undefined);
    this.#ui.hideLoading({ignoreError: true})
  }

  /**
   * Stops the flow with the error dialog. The title and message come from the error: the API's copy for a code it
   * sent, or `ClientErrorDisplays` for a BSR code the client raised. Callers choose the button, and pass
   * `options.copy` where they raise a server-owned code with no response behind it.
   */
  errorOccurred(error: unknown, options?: AppErrorOptions): IMessageCode {
    const messageCode = MapError(error, {noLog: true});
    const {title, message} = resolveErrorCopy(messageCode, options?.copy);

    const shouldCaptureStackFromCaller = !messageCode.cause || !(messageCode.cause instanceof Error);
    const appError = new AppErrorImpl(messageCode, title, message, options);
    appError.stack = messageCode.stack;
    appError.cause = messageCode.cause;
    if (shouldCaptureStackFromCaller) {
      tryCaptureStackTrace(appError, this.errorOccurred);
    }

    CaptureError(appError);

    this.#analytics.fireEvent(this.#nav.currentPage(), Analytics.EventType.Error, {
      action: 'code',
      data: messageCode.code
    }, {force: true});

    untracked(() => this.#error.set(appError));
    this.#ui.showError(appError,
      (messageCode.code === null ? undefined : SupportLinkClasses[messageCode.code]) ?? DefaultSupportLinkClass);
    untracked(() => this.#ui.sidebar.set(null));
    return appError.messageCode;
  }

  toggleBooking(custom?: boolean) {
    this.#data.update(data => {
      return {
        ...data,
        appt: {
          ...(data.appt ?? {} as SelectedAppointmentInfo),
          isBooked: custom ?? !data.appt?.isBooked,
        }
      };
    });
  }

  /**
   * A failed hold or intake update. The API's copy says what went wrong (ITK0013's names the referral number,
   * which `config.support` keeps on a referral visit); this only adds a button where there is somewhere to go.
   */
  showGeneralBookingError(error: IMessageCode) {
    let action: AppErrorOptions['action'] = null;
    let copy: AppErrorOptions['copy'];
    switch (error.code) {
      case MessageCode.ITK0004.code:
        // only ever arrives from the API, which words it about the date and time rather than the slot
        action = {label: ErrorActionLabels.ViewAvailableTimes, onClick: AppErrorAction.BackToFinder};
        break;
      case MessageCode.ITK0005.code:
        action = {label: ErrorActionLabels.ViewAvailableTimes, onClick: AppErrorAction.BackToFinder};
        copy = LocallyRaisedDisplays.slotTaken;
        break;
      case MessageCode.ATH0005.code:
        action = {label: ErrorActionLabels.StartNewSession, onClick: AppErrorAction.BackToFinder};
        // HandleNetworkErrors maps a 401/403 with no message code in its body to a bare ATH0005
        copy = LocallyRaisedDisplays.sessionExpired;
        break;
    }

    this.errorOccurred(error, {action, copy});
  }

  clinician = computed(() => {
    const clinician = this.#data().appt?.clinician;
    assertExists(clinician);
    return clinician;
  });

  appointment = computed(() => {
    const appt = this.#data().appt;
    assertExists(appt);
    return appt;
  });

  data = computed(() => {
    const data = this.#data();

    return {
      ...data,
      filters: {
        state: data.state,
        insuranceId: data.insurance?.primary?.id,
        insurancePlanId: data.insurance?.primary?.planId,
        ...data.filters,
      }
    }
  })

  choseLocationPreference(location: SearchFilter['location']) {
    this.#data.update(data => {
      return ({
        ...data,
        filters: {
          ...data.filters,
          location
        }
      });
    });
  }

  choseState(state: State) {
    if (state === this.#data().state) {
      return;
    }

    if (state === State.None) {
      this.choseInsurer(undefined);
    }


    // zero out the insurance and selected appointment
    this.#data.update(data => {
      return ({
        ...data,
        state,
        appt: undefined,
        insurance: {},
      });
    });
    return this.#loadInsurerOptions(state);
  }

  choseAgeRange(ageRange: SearchFilter['ageRange']) {
    this.#data.update(data => ({
      ...data,
      filters: {
        ...data.filters,
        ageRange
      }
    }));
  }

  chosePreferredLanguage(languageId: SearchFilter['languageId']) {
    this.#data.update(data => ({
      ...data,
      filters: {
        ...data.filters,
        languageId,
      }
    }));
  }

  changedCashSelfPayAttestation(attested?: boolean) {
    this.#data.update(data => {
      const primary = data.insurance?.primary;
      if (!primary) {
        return data;
      }
      return {
        ...data,
        insurance: {
          ...data.insurance,
          primary: {
            ...primary,
            cashSelfPayAttested: attested,
          }
        }
      };
    });
  }

  choseInsurer(insurerId?: number) {
    this.#data.update(data => {
      let primary = this.#data().insurance?.primary;
      const insurer = this.#options.insurers().find(i => i.id === insurerId);
      const plans = insurer?.plans ?? [];
      let planId = primary?.planId;

      if (insurer?.isCashSelfPay) {
        planId = plans.find(p => p.bookingStatusId === BookingStatusType.CashSelfPay)?.id
      } else if (!plans.find(p => p.id === primary?.planId)) {
        planId = undefined;
      }

      if (primary?.id === insurerId) {
        return data;
      }

      // Re-selecting the same insurer returned above, so this is a new insurer and needs a fresh attestation.
      primary = {
        ...primary,
        id: insurerId,
        planId,
        isCashSelfPay: insurer?.isCashSelfPay,
        cashSelfPayAttested: null
      };

      return {
        ...data,
        insurance: {
          primary,
        }
      };
    });
  }

  choseInsurerPlan(planId?: number) {
    this.#data.update(data => {
      let primary = this.#data().insurance?.primary;
      const insurer = this.#options.insurers().find(i => i.id === primary?.id);
      const plans = insurer?.plans ?? [];
      if (insurer?.isCashSelfPay) {
        planId = plans.find(p => p.bookingStatusId === BookingStatusType.CashSelfPay)?.id
      } else if (!plans.find(p => p.id === planId)) {
        planId = undefined;
      }
      if (primary?.planId === planId) {
        return data;
      }
      primary = {
        ...primary,
        planId,
      };

      return {
        ...data,
        insurance: {
          primary,
        }
      };
    });
  }

  clearAppointment() {
    this.#data.update(data => {
      return {
        ...data,
        appt: undefined
      };
    });
  }
  choseAppointment(appt: SelectedAppointmentInfo) {
    this.#data.update(data => ({
      ...data,
      appt
    }));
  }

  changedInsurance(target: 'primary', insurance: InsuranceInfo): void;
  changedInsurance(target: 'secondary', insurance: InsuranceInfo | undefined): void;
  changedInsurance(target: 'primary' | 'secondary', insurance: InsuranceInfo): void {

    this.#analytics.fireFieldChange(this.#nav.currentPage(), target === 'secondary' ? 'secondaryInsurance' : 'insurance', this.#data().insurance?.[target]?.id);

    if (target === 'primary') {
      this.#analytics.fireFieldChange(this.#nav.currentPage(), 'hasSecondary', this.#data().insurance?.hasSecondary ?? false);
    }
    this.#analytics.fireFieldChange(this.#nav.currentPage(), target === 'secondary' ? 'secondaryInsurancePlan' : 'insurancePlan', this.#data().insurance?.[target]?.planId);

    this.#data.update(data => {
      const current = data.insurance?.[target];
      if (current === insurance) {
        return data;
      }
      // this is a hack as updating the whole object causes infinite loop of form updates then saves then updates
      if (current && insurance) {

        let sameHolder: boolean;

        // if both holders are objects then check if they are the same
        if (!!current.holder && !!insurance.holder) {
          // check if the names are the same
          sameHolder = current.holder.name?.toLowerCase() === insurance.holder.name?.toLowerCase();

          // if the current holder has a dob then check if the insurance holder has a dob and if they are the same
          if (current.holder.dob) {
            sameHolder = sameHolder && !!insurance.holder.dob && current.holder.dob.equals(insurance.holder.dob);
          } else if (insurance.holder.dob) {
            // the current holder does not have a dob but the insurance holder does
            sameHolder = false;
          }
        } else {
          // set to true if both holders are falsy
          sameHolder = !current.holder && !insurance.holder;
        }


        if (current.id === insurance.id &&
          current.planId === insurance.planId &&
          current.groupNumber === insurance.groupNumber &&
          current.policyNumber === insurance.policyNumber &&
          sameHolder
        ) {
          return data;
        }
      }

      let hasSecondary = data.insurance?.hasSecondary;
      if (target === 'secondary') {
        hasSecondary = !!insurance;
      }

      // The form only sends what it shows, so the cash flags are decided here: the insurer says whether
      // it's cash self-pay, and the attestation survives anything but a change of insurer or plan.
      const insurer = this.#options.insurers().find(i => i.id === insurance?.id);
      const samePlan = current?.id === insurance?.id && current?.planId === insurance?.planId;
      const newInsurance = {
        ...data.insurance,
        [target]: insurance ? {
          ...insurance,
          isCashSelfPay: insurer?.isCashSelfPay,
          cashSelfPayAttested: samePlan ? current?.cashSelfPayAttested : null,
        } : undefined,
        hasSecondary: hasSecondary,
      } as NonNullable<AppData['insurance']>;
      if (!insurance && !hasSecondary) {
        delete newInsurance[target];
      }

      return {
        ...data,
        insurance: newInsurance
      };
    });
  }

  changedDemographics(demographics: DemographicsInfo) {

    this.#data.update(data => {
      const demographicsInfo = data.demographics ?? {};

      demographics = {
        ...demographicsInfo,
        ...demographics,
      };

      const newData = {
        ...data,
        state: demographics.state ?? data.state,
        demographics
      };

      return newData;
    });
  }

  choseCondition(conditionId: number) {
    this.#data.update(data => {
      return {
        ...data,
        filters: {
          ...data.filters,
          conditionId
        },
        appt: data.appt ? {
          ...data.appt,
          conditionId,
        } : undefined
      };
    });
  }

  choseDaysOfWeek(dayOfWeek: SearchFilter['dayOfWeek']) {
    this.#data.update(data => {
      return {
        ...data,
        filters: {
          ...data.filters,
          dayOfWeek
        }
      };
    });
  }

  choseWeeksOutPreference(weeksOut: SearchFilter['weeksOut']) {

    this.#data.update(data => {
      return {
        ...data,
        filters: {
          ...data.filters,
          weeksOut
        }
      };
    });
  }

  choseTimeOfDayPreference(timeOfDay: SearchFilter['timeOfDay']) {

    this.#data.update(data => {
      return {
        ...data,
        filters: {
          ...data.filters,
          timeOfDay
        }
      };
    });
  }

  choseTypeOfCare(typeOfCare: ReasonType.Therapy | ReasonType.Psychiatry | undefined) {

    this.#data.update(data => {
      return {
        ...data,
        filters: {
          ...data.filters,
          typeOfCare
        },
        appt: data.appt ? {
          ...data.appt,
          typeOfCare: typeOfCare ?? data.appt.typeOfCare,
        } : undefined
      };
    });
  }

  choseGenderPreference(genderId: number | undefined) {

    this.#data.update(data => {
      return {
        ...data,
        filters: {
          ...data.filters,
          genderId
        }
      };
    });
  }

  choseSettingPreference(setting: AppointmentSettingType | undefined) {

    this.#data.update(data => {
      return {
        ...data,
        filters: {
          ...data.filters,
          setting
        },
      };
    });
  }

  choseAppointmentSetting(setting: AppointmentSettingType.InPerson | AppointmentSettingType.Telehealth | undefined) {
    this.#data.update(data => {
      assertExists(data.appt);
      return {
        ...data,
        appt: {
          ...data.appt,
          chosenSetting: setting!,
        }
      };
    });
  }

  clinicianSearchChanged(clinician: string) {
    this.#data.update(data => {
      return {
        ...data,
        filters: {
          ...data.filters,
          clinician
        }
      };
    });
  }

  selectedEligibility(id: string, value: boolean | undefined) {
    id = id.toLowerCase();
    this.#data.update(data => {
      const eligibility = {...data.eligibility};
      if (value === undefined) {
        delete eligibility[id];
      } else {
        eligibility[id] = value;
      }
      return {
        ...data,
        eligibility
      };
    });
  }

}

function toOptionItem<T extends {
  name: string,
  id: number,
  isPinned?: boolean,
  sortOrder?: number,
  description?: string
}>(item: T) {
  return {
    label: item.name,
    value: item.id,
    description: item.description,
    isPinned: item.isPinned,
    sortOrder: item.sortOrder,
    meta: item
  } satisfies Option<number, T>;
}

type compareItem = { name: string, isPinned?: boolean, sortOrder?: number } | {
  label: string,
  isPinned?: boolean,
  sortOrder?: number
};

function compareItem(a: compareItem, b: compareItem) {
  if (a.isPinned && !b.isPinned) {
    return -1;
  }
  if (b.isPinned && !a.isPinned) {
    return 1;
  }

  const aSort = a.sortOrder;
  const bSort = b.sortOrder;

  const aHasSort = typeof aSort === 'number';
  const bHasSort = typeof bSort === 'number';

  if (aHasSort && bHasSort) {
    return aSort - bSort;
  }
  if (aHasSort && !bHasSort) {
    return -1;
  }
  if (!aHasSort && bHasSort) {
    return 1;
  }
  const nameA = 'name' in a ? a.name : a.label;
  const nameB = 'name' in b ? b.name : b.label;
  return nameA.localeCompare(nameB);
}

export interface AppData {
  readonly state: State;
  readonly filters: SearchFilter;
  readonly appt?: SelectedAppointmentInfo;
  readonly demographics?: DemographicsInfo;
  readonly insurance: {
    readonly primary?: InsuranceInfo,
    readonly hasSecondary?: boolean,
    readonly secondary?: InsuranceInfo
  };
  readonly nav: NavInfo;
  readonly eligibility?: Readonly<Record<string, boolean>>;
}


// interface AppErrorActions {
//
// }

